import { geoNaturalEarth1, geoPath, geoCentroid, geoGraticule10 } from "d3-geo";
import { splitTerritories, prettyDate } from "@/lib/geo";
import { yearStats } from "@/lib/yearReview";

export const W = 1080;
export const H = 1920;
export const SQ = 1080;
const PORTRAIT = { h: H, ext: [[-260, 330], [W + 260, 1060]], glowY: 690, fade: [860, 1120], top: 420, ctaY: 1628, ctaH: 112, ctaFont: 44 };
const SQUARE = { h: SQ, ext: [[-120, 200], [W + 120, 640]], glowY: 420, fade: [520, 690], top: 230, ctaY: 912, ctaH: 96, ctaFont: 38 };
const C = { bg: "#0B0F17", land: "#1A2333", border: "rgba(148,163,184,0.18)", visited: "#0F7048", wish: "rgba(245,158,11,0.55)", neon: "#39FF88", amber: "#F59E0B", rose: "#FB7185", text: "#F8FAFC", muted: "#94A3B8" };
const SHORT = { "United States of America": "USA", "United Kingdom": "UK", "United Arab Emirates": "UAE", "Dem. Rep. Congo": "DR Congo", "Central African Rep.": "C. African Rep.", "Bosnia and Herz.": "Bosnia & Herz." };
const short = (n) => SHORT[n] || n;
const DISPLAY = '"Bricolage Grotesque", sans-serif';
const BODY = "Figtree, sans-serif";
const MONO = '"JetBrains Mono", monospace';

export const loadFonts = () =>
  Promise.all([
    document.fonts.load(`700 90px ${DISPLAY}`),
    document.fonts.load(`600 44px ${DISPLAY}`),
    document.fonts.load(`500 34px ${BODY}`),
    document.fonts.load(`500 26px ${MONO}`),
  ]).catch(() => null);

const wrap = (ctx, text, maxW, maxLines) => {
  const lines = [];
  let line = "";
  for (const w of text.split(" ")) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
  }
  lines.push(line);
  if (lines.length <= maxLines) return lines;
  const out = lines.slice(0, maxLines);
  let last = out[maxLines - 1];
  while (ctx.measureText(`${last}…`).width > maxW && last.length) last = last.slice(0, -1);
  out[maxLines - 1] = `${last.trim()}…`;
  return out;
};

const visitedCityPoints = (geo, visited) =>
  visited.flatMap((v) => v.cities.map((name) => {
    const base = geo.cities.find((c) => c.n === name && c.c === v.country);
    if (base) return [base.x, base.y];
    const p = (v.places || []).find((x) => x.name === name);
    return p ? [p.x, p.y] : null;
  })).filter(Boolean);

const drawMap = (ctx, geo, { visited, wishlist, highlight, cityVisited }, L = PORTRAIT) => {
  const visitedSet = new Set(visited.map((v) => v.country));
  const wishSet = new Set(wishlist.map((w) => w.country));
  const feats = geo.features.filter((f) => visitedSet.has(f.properties.name));
  const lon = feats.length ? feats.reduce((s, f) => s + geoCentroid(f)[0], 0) / feats.length : 10;
  const proj = geoNaturalEarth1().rotate([-lon, 0]).fitExtent(L.ext, { type: "Sphere" });
  const path = geoPath(proj, ctx);

  ctx.save();
  const glow = ctx.createRadialGradient(W / 2, L.glowY, 50, W / 2, L.glowY, 700);
  glow.addColorStop(0, "rgba(16,185,129,0.16)");
  glow.addColorStop(1, "rgba(16,185,129,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, L.h);

  ctx.beginPath(); path(geoGraticule10());
  ctx.strokeStyle = "rgba(148,163,184,0.06)"; ctx.lineWidth = 1; ctx.stroke();

  geo.features.forEach((f) => {
    const name = f.properties.name;
    const { core, overseas } = splitTerritories(f);
    const hi = highlight?.has(name);
    ctx.beginPath(); path(core);
    ctx.fillStyle = hi ? "#12A36A" : visitedSet.has(name) ? C.visited : wishSet.has(name) ? C.wish : C.land;
    ctx.fill();
    ctx.strokeStyle = visitedSet.has(name) ? "rgba(16,185,129,0.6)" : C.border; ctx.lineWidth = 1; ctx.stroke();
    if (overseas) { ctx.beginPath(); path(overseas); ctx.fillStyle = C.land; ctx.fill(); }
  });

  visitedCityPoints(geo, cityVisited || visited).forEach(([x, y]) => {
    const p = proj([x, y]);
    if (!p) return;
    const g = ctx.createRadialGradient(p[0], p[1], 0, p[0], p[1], 26);
    g.addColorStop(0, "rgba(57,255,136,0.65)"); g.addColorStop(1, "rgba(57,255,136,0)");
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(p[0], p[1], 26, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = C.neon; ctx.beginPath(); ctx.arc(p[0], p[1], 5, 0, Math.PI * 2); ctx.fill();
  });

  const [f0, f1] = L.fade;
  const fade = ctx.createLinearGradient(0, f0, 0, f1);
  fade.addColorStop(0, "rgba(11,15,23,0)"); fade.addColorStop(1, C.bg);
  ctx.fillStyle = fade; ctx.fillRect(0, f0, W, f1 - f0);
  ctx.fillStyle = C.bg; ctx.fillRect(0, f1 - 1, W, L.h - f1 + 1);
  const low = ctx.createRadialGradient(W / 2, L.h, 50, W / 2, L.h, 900);
  low.addColorStop(0, "rgba(16,185,129,0.10)"); low.addColorStop(1, "rgba(16,185,129,0)");
  ctx.fillStyle = low; ctx.fillRect(0, f1 - 1, W, L.h - f1 + 1);
  const top = ctx.createLinearGradient(0, 0, 0, L.top);
  top.addColorStop(0, C.bg); top.addColorStop(1, "rgba(11,15,23,0)");
  ctx.fillStyle = top; ctx.fillRect(0, 0, W, L.top);
  ctx.restore();
};

const grain = (ctx) => {
  const img = ctx.getImageData(0, 0, W, ctx.canvas.height);
  for (let i = 0; i < img.data.length; i += 4) {
    const n = (Math.random() - 0.5) * 10;
    img.data[i] += n; img.data[i + 1] += n; img.data[i + 2] += n;
  }
  ctx.putImageData(img, 0, 0);
};

const header = (ctx, title, stats) => {
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = C.muted; ctx.font = `500 24px ${MONO}`;
  ctx.fillText("THE BUCKETLIST", 80, 270);
  ctx.fillStyle = C.text; ctx.font = `700 92px ${DISPLAY}`;
  const lines = wrap(ctx, title, W - 160, 2);
  lines.forEach((l, i) => ctx.fillText(l, 80, 370 + i * 96));
  ctx.fillStyle = C.neon; ctx.font = `500 26px ${MONO}`;
  ctx.fillText(stats, 80, 370 + lines.length * 96 - 30);
};

// Drawn call-to-action; place an Instagram/Facebook link sticker on top to make it tappable
const footer = (ctx, link, L = PORTRAIT, label = "See my bucket list") => {
  const bw = L === SQUARE ? 680 : 760, bh = L.ctaH, bx = (W - bw) / 2, by = L.ctaY;
  const g = ctx.createLinearGradient(bx, 0, bx + bw, 0);
  g.addColorStop(0, "#34D399"); g.addColorStop(1, "#38BDF8");
  ctx.save();
  ctx.shadowColor = "rgba(52,211,153,0.35)"; ctx.shadowBlur = 40;
  ctx.fillStyle = g; ctx.beginPath(); ctx.roundRect(bx, by, bw, bh, bh / 2); ctx.fill();
  ctx.restore();
  ctx.textAlign = "center";
  ctx.fillStyle = "#04130C"; ctx.font = `700 ${L.ctaFont}px ${DISPLAY}`;
  const tw = ctx.measureText(label).width;
  ctx.fillText(label, W / 2 - 22, by + bh / 2 + L.ctaFont * 0.32);
  const ax = W / 2 - 22 + tw / 2 + 24, ay = by + bh / 2;
  ctx.strokeStyle = "#04130C"; ctx.lineWidth = 5; ctx.lineCap = "round"; ctx.lineJoin = "round";
  ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(ax + 30, ay); ctx.moveTo(ax + 18, ay - 12); ctx.lineTo(ax + 30, ay); ctx.lineTo(ax + 18, ay + 12); ctx.stroke();
  ctx.fillStyle = "rgba(248,250,252,0.85)"; ctx.font = `500 26px ${MONO}`;
  ctx.fillText((link || "mybucketlist.is").replace(/^https?:\/\//, "").replace(/\?.*$/, ""), W / 2, by + bh + (L === SQUARE ? 42 : 52));
  ctx.textAlign = "left";
};

const sectionLabel = (ctx, text, x, y, color) => {
  ctx.fillStyle = color; ctx.fillRect(x, y - 18, 28, 4);
  ctx.font = `500 24px ${MONO}`; ctx.fillText(text.toUpperCase(), x + 42, y - 8);
};

const column = (ctx, { x, y, w, label, color, items, numbered }) => {
  sectionLabel(ctx, label, x, y, color);
  let cy = y + 60;
  ctx.font = `600 ${numbered ? 42 : 34}px ${numbered ? DISPLAY : BODY}`;
  const fits = items.reduce((h, t) => h + 54 + wrap(ctx, t, w - 56, 2).length * 42, cy) - 54 <= 1600;
  items.forEach((t, i) => {
    if (numbered) { ctx.fillStyle = color; ctx.font = `500 26px ${MONO}`; ctx.fillText(String(i + 1).padStart(2, "0"), x, cy); }
    else { ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(x + 12, cy - 9, 11, 0, Math.PI * 2); ctx.stroke(); }
    ctx.fillStyle = C.text; ctx.font = `600 ${numbered ? 42 : 34}px ${numbered ? DISPLAY : BODY}`;
    const lines = wrap(ctx, t, w - 56, fits ? 2 : 1);
    lines.forEach((l, j) => ctx.fillText(l, x + 56, cy + j * 42));
    cy += 54 + lines.length * 42;
  });
  if (!items.length) { ctx.fillStyle = C.muted; ctx.font = `500 28px ${BODY}`; ctx.fillText("Still dreaming…", x, cy); }
};

export const drawDreams = (ctx, geo, data, opts) => {
  const { title, goals, withGrain } = opts;
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
  drawMap(ctx, geo, data);
  const cities = data.visited.reduce((s, v) => s + v.cities.length, 0);
  header(ctx, title, `${data.visited.length} COUNTRIES · ${cities} CITIES · ${data.goals.filter((g) => g.done).length} DREAMS DONE`);
  const wish = (opts.wish || data.wishlist.slice(0, 5).map((w) => w.country)).map(short);
  column(ctx, { x: 80, y: 1130, w: 430, label: "Where I'm going", color: C.amber, items: wish, numbered: true });
  column(ctx, { x: 570, y: 1130, w: 430, label: "Before I die", color: C.rose, items: goals.map((g) => g.title), numbered: false });
  footer(ctx, opts.link);
  if (withGrain) grain(ctx);
};

const KIND_LABEL = { country: "COUNTRY", city: "CITY", event: "MOMENT" };

export const drawBestOf = (ctx, geo, data, { title, link, withGrain }) => {
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
  const highlight = new Set(data.top5.slice(0, 5).map((t) => (t.kind === "country" ? t.title : t.kind === "city" ? t.subtitle : null)).filter(Boolean));
  drawMap(ctx, geo, { ...data, highlight });
  header(ctx, title, `${data.visited.length} COUNTRIES EXPLORED`);
  sectionLabel(ctx, "My top 5, ever", 80, 1130, C.neon);
  let y = 1200;
  data.top5.slice(0, 5).forEach((t, i) => {
    ctx.fillStyle = "rgba(57,255,136,0.18)"; ctx.font = `700 96px ${DISPLAY}`;
    ctx.fillText(String(i + 1), 72, y + 34);
    ctx.fillStyle = C.text; ctx.font = `600 46px ${DISPLAY}`;
    const line = wrap(ctx, t.title, W - 300, 1)[0];
    ctx.fillText(line, 170, y);
    ctx.fillStyle = C.muted; ctx.font = `500 22px ${MONO}`;
    ctx.fillText([KIND_LABEL[t.kind], t.subtitle].filter(Boolean).join(" · ").toUpperCase(), 172, y + 38);
    y += 90;
  });
  if (!data.top5.length) { ctx.fillStyle = C.muted; ctx.font = `500 30px ${BODY}`; ctx.fillText("Pick your top 5 on your page first.", 80, y); }
  footer(ctx, link);
  if (withGrain) grain(ctx);
};

const bigStat = (ctx, x, value, label, color) => {
  ctx.fillStyle = color; ctx.font = `700 120px ${DISPLAY}`;
  ctx.fillText(String(value), x, 1240);
  ctx.fillStyle = C.muted; ctx.font = `500 22px ${MONO}`;
  ctx.fillText(label, x + 4, 1282);
};

export const drawYear = (ctx, geo, data, { title, year, link, withGrain }) => {
  const st = yearStats(data, year);
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
  drawMap(ctx, geo, { ...data, highlight: new Set(st.countries.map((c) => c.country)), cityVisited: st.cityVisited });

  ctx.fillStyle = C.muted; ctx.font = `500 24px ${MONO}`;
  ctx.fillText("THE BUCKETLIST · YEAR IN REVIEW", 80, 270);
  const grad = ctx.createLinearGradient(80, 0, 900, 0);
  grad.addColorStop(0, C.neon); grad.addColorStop(1, "#7DD3FC");
  ctx.fillStyle = grad; ctx.font = `700 250px ${DISPLAY}`;
  ctx.fillText(year, 64, 500);
  ctx.fillStyle = C.text; ctx.font = `600 52px ${DISPLAY}`;
  ctx.fillText(wrap(ctx, title, W - 160, 1)[0], 80, 580);

  bigStat(ctx, 80, st.countries.length, st.firstTime ? `COUNTRIES · ${st.firstTime} NEW` : "COUNTRIES", C.neon);
  bigStat(ctx, 420, st.cities, "CITIES", "#7DD3FC");
  bigStat(ctx, 720, st.dreams.length, "DREAMS DONE", C.rose);

  sectionLabel(ctx, "Where I went", 80, 1390, C.neon);
  let y = 1450;
  st.countries.slice(0, 5).forEach((c) => {
    ctx.fillStyle = C.muted; ctx.font = `500 22px ${MONO}`;
    ctx.fillText(prettyDate(c.date, "MMM").toUpperCase(), 80, y);
    ctx.fillStyle = C.text; ctx.font = `600 36px ${DISPLAY}`;
    ctx.fillText(wrap(ctx, short(c.country), 330, 1)[0], 160, y + 2);
    y += 56;
  });
  if (!st.countries.length) { ctx.fillStyle = C.muted; ctx.font = `500 28px ${BODY}`; ctx.fillText("Add visit dates to fill this in", 80, y); }

  sectionLabel(ctx, "Dreams I lived", 570, 1390, C.rose);
  y = 1450;
  st.dreams.slice(0, 4).forEach((g) => {
    ctx.fillStyle = C.rose; ctx.beginPath(); ctx.arc(582, y - 10, 11, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = C.bg; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(576, y - 10); ctx.lineTo(581, y - 5); ctx.lineTo(589, y - 15); ctx.stroke();
    ctx.fillStyle = C.text; ctx.font = `500 30px ${BODY}`;
    const lines = wrap(ctx, g.title, 380, 2);
    lines.forEach((l, j) => ctx.fillText(l, 610, y + j * 36));
    y += 24 + lines.length * 36;
  });
  if (!st.dreams.length) { ctx.fillStyle = C.muted; ctx.font = `500 28px ${BODY}`; ctx.fillText("Next year's the one", 570, y); }

  footer(ctx, link);
  if (withGrain) grain(ctx);
};

export const drawTop10 = (ctx, geo, data, { title, link, withGrain }) => {
  const top = data.wishlist.slice(0, 10);
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, H);
  drawMap(ctx, geo, { ...data, wishlist: top });
  header(ctx, title, `${data.wishlist.length} PLACES ON MY LIST · ${data.visited.length} ALREADY EXPLORED`);
  sectionLabel(ctx, "My top 10 countries", 80, 1130, C.amber);
  top.forEach((w, i) => {
    const x = i < 5 ? 80 : 570;
    const y = 1210 + (i % 5) * 92;
    ctx.fillStyle = C.amber; ctx.font = `500 26px ${MONO}`;
    ctx.fillText(String(i + 1).padStart(2, "0"), x, y);
    ctx.fillStyle = C.text; ctx.font = `600 42px ${DISPLAY}`;
    ctx.fillText(wrap(ctx, short(w.country), 370, 1)[0], x + 60, y);
    if (w.cities?.length) {
      ctx.fillStyle = C.muted; ctx.font = `500 20px ${MONO}`;
      ctx.fillText(wrap(ctx, w.cities.slice(0, 2).join(" · ").toUpperCase(), 370, 1)[0], x + 62, y + 32);
    }
  });
  if (!top.length) { ctx.fillStyle = C.muted; ctx.font = `500 30px ${BODY}`; ctx.fillText("Add countries to your Want to go list first.", 80, 1210); }
  footer(ctx, link);
  if (withGrain) grain(ctx);
};

// Square 1080x1080 feed versions
const sqHeader = (ctx, title, stats, eyebrow = "THE BUCKETLIST") => {
  ctx.fillStyle = C.muted; ctx.font = `500 22px ${MONO}`;
  ctx.fillText(eyebrow, 72, 84);
  ctx.fillStyle = C.text; ctx.font = `700 66px ${DISPLAY}`;
  ctx.fillText(wrap(ctx, title, W - 144, 1)[0], 72, 156);
  if (stats) { ctx.fillStyle = C.neon; ctx.font = `500 22px ${MONO}`; ctx.fillText(stats, 72, 198); }
};

const sqRows = (ctx, { x, y, w, items, color, numbered, step = 50, size = 32 }) => {
  items.forEach((t, i) => {
    const cy = y + i * step;
    if (numbered) { ctx.fillStyle = color; ctx.font = `500 22px ${MONO}`; ctx.fillText(String(i + 1).padStart(2, "0"), x, cy); }
    else { ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(x + 10, cy - 9, 9, 0, Math.PI * 2); ctx.stroke(); }
    ctx.fillStyle = C.text; ctx.font = `600 ${size}px ${numbered ? DISPLAY : BODY}`;
    ctx.fillText(wrap(ctx, t, w - 50, 1)[0], x + 48, cy);
  });
};

const sqStart = (ctx, geo, data) => {
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, SQ);
  drawMap(ctx, geo, data, SQUARE);
};

const sqEnd = (ctx, link, withGrain) => { footer(ctx, link, SQUARE); if (withGrain) grain(ctx); };

export const drawDreamsSquare = (ctx, geo, data, { title, goals, wish, link, withGrain }) => {
  sqStart(ctx, geo, data);
  const cities = data.visited.reduce((s, v) => s + v.cities.length, 0);
  sqHeader(ctx, title, `${data.visited.length} COUNTRIES · ${cities} CITIES · ${data.goals.filter((g) => g.done).length} DREAMS DONE`);
  const places = (wish || data.wishlist.slice(0, 5).map((w) => w.country)).map(short).slice(0, 3);
  sectionLabel(ctx, "Where I'm going", 72, 712, C.amber);
  sqRows(ctx, { x: 72, y: 768, w: 440, items: places, color: C.amber, numbered: true });
  sectionLabel(ctx, "Before I die", 570, 712, C.rose);
  sqRows(ctx, { x: 570, y: 768, w: 440, items: goals.map((g) => g.title).slice(0, 3), color: C.rose, size: 28 });
  sqEnd(ctx, link, withGrain);
};

export const drawTop10Square = (ctx, geo, data, { title, link, withGrain }) => {
  const top = data.wishlist.slice(0, 10);
  sqStart(ctx, geo, { ...data, wishlist: top });
  sqHeader(ctx, title, `${data.wishlist.length} PLACES ON MY LIST · ${data.visited.length} EXPLORED`);
  sectionLabel(ctx, "My top 10 countries", 72, 712, C.amber);
  sqRows(ctx, { x: 72, y: 760, w: 450, items: top.slice(0, 5).map((w) => short(w.country)), color: C.amber, numbered: true, step: 34, size: 28 });
  ctx.save(); ctx.translate(0, 0);
  top.slice(5).forEach((w, i) => {
    const cy = 760 + i * 34;
    ctx.fillStyle = C.amber; ctx.font = `500 22px ${MONO}`; ctx.fillText(String(i + 6).padStart(2, "0"), 570, cy);
    ctx.fillStyle = C.text; ctx.font = `600 28px ${DISPLAY}`; ctx.fillText(wrap(ctx, short(w.country), 400, 1)[0], 618, cy);
  });
  ctx.restore();
  if (!top.length) { ctx.fillStyle = C.muted; ctx.font = `500 26px ${BODY}`; ctx.fillText("Add countries to your Want to go list first.", 72, 770); }
  sqEnd(ctx, link, withGrain);
};

export const drawBestOfSquare = (ctx, geo, data, { title, link, withGrain }) => {
  const items = data.top5.slice(0, 5);
  const highlight = new Set(items.map((t) => (t.kind === "country" ? t.title : t.kind === "city" ? t.subtitle : null)).filter(Boolean));
  sqStart(ctx, geo, { ...data, highlight });
  sqHeader(ctx, title, `${data.visited.length} COUNTRIES EXPLORED`);
  sectionLabel(ctx, "My top 5, ever", 72, 712, C.neon);
  items.forEach((t, i) => {
    const x = i < 3 ? 72 : 570, y = 772 + (i % 3) * 46;
    ctx.fillStyle = C.neon; ctx.font = `700 30px ${DISPLAY}`; ctx.fillText(String(i + 1), x, y);
    ctx.fillStyle = C.text; ctx.font = `600 30px ${DISPLAY}`; ctx.fillText(wrap(ctx, t.title, 400, 1)[0], x + 40, y);
  });
  if (!items.length) { ctx.fillStyle = C.muted; ctx.font = `500 26px ${BODY}`; ctx.fillText("Pick your top 5 on your page first.", 72, 772); }
  sqEnd(ctx, link, withGrain);
};

export const drawYearSquare = (ctx, geo, data, { title, year, link, withGrain }) => {
  const st = yearStats(data, year);
  sqStart(ctx, geo, { ...data, highlight: new Set(st.countries.map((c) => c.country)), cityVisited: st.cityVisited });
  ctx.fillStyle = C.muted; ctx.font = `500 22px ${MONO}`; ctx.fillText("THE BUCKETLIST · YEAR IN REVIEW", 72, 84);
  const grad = ctx.createLinearGradient(72, 0, 700, 0);
  grad.addColorStop(0, C.neon); grad.addColorStop(1, "#7DD3FC");
  ctx.fillStyle = grad; ctx.font = `700 150px ${DISPLAY}`; ctx.fillText(year, 60, 222);
  ctx.fillStyle = C.text; ctx.font = `600 38px ${DISPLAY}`; ctx.fillText(wrap(ctx, title, W - 144, 1)[0], 72, 274);
  [[72, st.countries.length, st.firstTime ? `COUNTRIES · ${st.firstTime} NEW` : "COUNTRIES", C.neon], [420, st.cities, "CITIES", "#7DD3FC"], [720, st.dreams.length, "DREAMS DONE", C.rose]]
    .forEach(([x, v, l, c]) => { ctx.fillStyle = c; ctx.font = `700 90px ${DISPLAY}`; ctx.fillText(String(v), x, 790); ctx.fillStyle = C.muted; ctx.font = `500 20px ${MONO}`; ctx.fillText(l, x + 4, 822); });
  ctx.fillStyle = C.text; ctx.font = `500 26px ${BODY}`;
  const went = st.countries.slice(0, 6).map((c) => short(c.country)).join(" · ");
  ctx.fillText(wrap(ctx, went || "Add visit dates to fill this in", W - 144, 1)[0], 72, 874);
  sqEnd(ctx, link, withGrain);
};

// Part 2 of a two-part story: the invite to sign up
const drawJoinIn = (ctx, geo, data, { title, link, withGrain }, L) => {
  const sq = L === SQUARE;
  ctx.fillStyle = C.bg; ctx.fillRect(0, 0, W, L.h);
  drawMap(ctx, geo, data, L);
  const first = (title || "").trim();
  ctx.fillStyle = C.muted; ctx.font = `500 ${sq ? 22 : 26}px ${MONO}`;
  ctx.fillText("THE BUCKETLIST", sq ? 72 : 80, sq ? 84 : 250);
  ctx.fillStyle = C.text; ctx.font = `700 ${sq ? 70 : 96}px ${DISPLAY}`;
  ctx.fillText("Make your own", sq ? 72 : 80, sq ? 166 : 360);
  ctx.fillStyle = C.neon; ctx.fillText("bucket list.", sq ? 72 : 80, sq ? 244 : 466);
  const y0 = sq ? 726 : 1220;
  ctx.fillStyle = C.text; ctx.font = `500 ${sq ? 30 : 40}px ${BODY}`;
  [["Map every country you've been to", C.neon], ["Rank where you want to go next", C.amber], ["Keep a list of things to do before you die", C.rose]].forEach(([t, c], i) => {
    const y = y0 + i * (sq ? 50 : 76);
    ctx.fillStyle = c; ctx.beginPath(); ctx.arc((sq ? 72 : 80) + 10, y - (sq ? 10 : 13), sq ? 7 : 9, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = C.text; ctx.fillText(wrap(ctx, t, W - 200, 1)[0], (sq ? 72 : 80) + 40, y);
  });
  if (first) { ctx.fillStyle = C.muted; ctx.font = `500 ${sq ? 22 : 28}px ${MONO}`; ctx.fillText(wrap(ctx, first.toUpperCase(), W - 160, 1)[0], sq ? 72 : 80, y0 + (sq ? 160 : 260)); }
  footer(ctx, link, L, "Make my own bucket list");
  if (withGrain) grain(ctx);
};
export const drawJoin = (ctx, geo, data, o) => drawJoinIn(ctx, geo, data, o, PORTRAIT);
export const drawJoinSquare = (ctx, geo, data, o) => drawJoinIn(ctx, geo, data, o, SQUARE);
